/** @jsxImportSource @emotion/react */
import type { ReactElement } from 'react'
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  CalendarOutlined,
  TableOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'

import {
  font,
  weightBold,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'
import type { GameViewMode } from '../hooks/useGameViewMode'
import { gameViewsLabel } from '../utils/i18n'

interface ViewSwitcherProps {
  mode: GameViewMode
  onChange: (mode: GameViewMode) => void
  disabled?: boolean
  /** Details is hidden on small screens — it cannot render honestly there. */
  showDetails?: boolean
  /**
   * `filters` matches the 40px filter-row controls it sits beside (Oldest /
   * Newest, Past / Today / Upcoming). `subTabs` matches the shorter Live /
   * Scheduler Admin control on the Schedule page. Same widget, two hosts, two
   * sets of neighbours — sized to whichever it is standing next to.
   */
  size?: 'filters' | 'subTabs'
}

const SIZES = {
  filters: {
    height: 40,
    containerHeight: 40,
    fontSize: 14,
    minWidth: 104,
    icon: 14,
  },
  //  Mirrors ScheduleSubTabs' own tab metrics exactly. Its container sits 2px
  //  taller than its tabs because the 1px border is outside them — matching the
  //  tab height alone still left the two groups visibly different heights.
  subTabs: {
    height: 33.6,
    containerHeight: 35.6,
    fontSize: 13,
    minWidth: 0,
    icon: 16,
  },
} as const

interface Tab {
  value: GameViewMode
  labelKey: string
  icon: ReactElement
}

const ALL_TABS: Tab[] = [
  { value: 'list', labelKey: 'gameViewList', icon: <UnorderedListOutlined /> },
  {
    value: 'calendar',
    labelKey: 'gameViewCalendar',
    icon: <CalendarOutlined />,
  },
  { value: 'details', labelKey: 'gameViewDetails', icon: <TableOutlined /> },
]

/**
 * The one dataset, three ways. Deliberately built in the house
 * `TimePeriodFilter` language — same 40px segmented control, same radii, same
 * active fill — so it reads as part of the existing filter row rather than as a
 * new widget bolted above it.
 */
const ViewSwitcher = ({
  mode,
  onChange,
  disabled = false,
  showDetails = true,
  size = 'filters',
}: ViewSwitcherProps) => {
  'use memo'

  const tabs = showDetails
    ? ALL_TABS
    : ALL_TABS.filter((tab) => tab.value !== 'details')

  const metrics = SIZES[size]

  return (
    <div
      role='group'
      aria-label={gameViewsLabel('gameViewSwitcherLabel')}
      css={containerStyles(disabled, metrics.containerHeight)}
    >
      {tabs.map((tab, index) => {
        const isActive = mode === tab.value
        return (
          <button
            key={tab.value}
            type='button'
            disabled={disabled}
            // `aria-pressed` rather than `aria-selected`: these are toggle
            // buttons in a group, not tabs owning tabpanels.
            aria-pressed={isActive}
            data-testid={`game-view-${tab.value}`}
            onClick={() => !disabled && onChange(tab.value)}
            css={tabStyles({
              isActive,
              isFirst: index === 0,
              isLast: index === tabs.length - 1,
              disabled,
              metrics,
            })}
          >
            <span aria-hidden='true'>{tab.icon}</span>
            {gameViewsLabel(tab.labelKey)}
          </button>
        )
      })}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = (disabled: boolean, height: number) => css`
  display: flex;
  align-items: center;
  gap: 0px;
  background-color: transparent;
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  height: ${height}px;
  opacity: ${disabled ? 0.5 : 1};
  transition: opacity 0.2s ease;
`

const tabStyles = ({
  isActive,
  isFirst,
  isLast,
  disabled,
  metrics,
}: {
  isActive: boolean
  isFirst: boolean
  isLast: boolean
  disabled: boolean
  metrics: (typeof SIZES)[keyof typeof SIZES]
}) => css`
  ${font.body}
  ${isActive
    ? metrics.minWidth === 0
      ? weightSemiBold
      : weightBold
    : weightRegular}
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: ${metrics.minWidth}px;
  height: ${metrics.height}px;
  padding: 0 12px;
  border-radius: ${isFirst ? '8px 0 0 8px' : isLast ? '0 8px 8px 0' : '0'};
  background-color: ${isActive ? colors.SECONDARY_300 : 'transparent'};
  border: none;
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: ${metrics.fontSize}px;
  line-height: 1;
  color: ${colors.NEUTRAL_0};

  ${!disabled &&
  css`
    &:hover {
      background-color: ${isActive
        ? colors.SECONDARY_300
        : colors.SECONDARY_200};
    }
    &:active {
      transform: scale(0.98);
    }
  `}

  .anticon {
    font-size: ${metrics.icon}px;
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: -2px;
  }
`

export default ViewSwitcher
